import createQrCode from "qrcode-generator";

/**
 * QR codes for booking links. This is the only file importing `qrcode-generator`: everything else works
 * on the plain module matrix, so a code renders as ordinary SVG (no `data:` or `blob:` image to allow).
 */

/** `true` is a dark module; `matrix[row][column]`, without the quiet zone. */
export type QrMatrix = boolean[][];

/** The white margin scanners need, in modules (the QR spec's minimum). */
export const QR_QUIET_ZONE = 4;

/** Encode a URL at error-correction M, which leaves room for scuffed table cards and worn stickers. */
export function qrMatrix(text: string): QrMatrix {
  const qr = createQrCode(0, "M");
  qr.addData(text, "Byte");
  qr.make();
  const size = qr.getModuleCount();
  return Array.from({ length: size }, (_row, row) =>
    Array.from({ length: size }, (_column, column) => qr.isDark(row, column)),
  );
}

/** The square viewBox side, in modules, including the quiet zone. */
export function qrViewBoxSize(matrix: QrMatrix): number {
  return matrix.length + QR_QUIET_ZONE * 2;
}

/** One SVG path covering every dark module, offset by the quiet zone. */
export function qrSvgPath(matrix: QrMatrix): string {
  return matrix
    .flatMap((cells, row) =>
      cells.flatMap((dark, column) =>
        dark ? [`M${column + QR_QUIET_ZONE} ${row + QR_QUIET_ZONE}h1v1h-1z`] : [],
      ),
    )
    .join("");
}

/** A complete standalone SVG document for download. Built only from the matrix, so it holds no user text. */
export function qrSvgDocument(matrix: QrMatrix): string {
  const size = qrViewBoxSize(matrix);
  return (
    `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${size} ${size}" shape-rendering="crispEdges">` +
    `<rect width="${size}" height="${size}" fill="#fff"/>` +
    `<path d="${qrSvgPath(matrix)}" fill="#000"/></svg>`
  );
}
